import {
  GamepadAxes,
  GamepadButtonName,
  GamepadButtonState,
  GamepadConnectionEvent,
  GamepadSnapshot,
  GAMEPAD_BUTTON_NAMES,
} from './GamepadApi'

/**
 * Common reader contract shared by Tier 1 (`WebGamepadReader`) and the Tier-2
 * test/native fakes. `RpcGamepadApi` drives whichever reader is active.
 */
export interface GamepadReader {
  start(
    onSnapshot: (snapshot: GamepadSnapshot) => void,
    onConnected: (event: GamepadConnectionEvent) => void,
    onDisconnected: (event: GamepadConnectionEvent) => void,
  ): void
  read(): GamepadSnapshot[]
  stop(): void
}

/**
 * W3C standard-mapping button order (indices into `Gamepad.buttons`). `null`
 * slots are reserved/unused in the standard layout.
 * https://www.w3.org/TR/gamepad/#remapping
 */
const STANDARD_BUTTON_ORDER: ReadonlyArray<GamepadButtonName | null> = [
  'a',
  'b',
  'x',
  'y',
  'leftBumper',
  'rightBumper',
  'leftTrigger',
  'rightTrigger',
  'select',
  'start',
  'leftStick',
  'rightStick',
  'dpadUp',
  'dpadDown',
  'dpadLeft',
  'dpadRight',
  'guide',
]

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function emptyButtons(): Record<GamepadButtonName, GamepadButtonState> {
  const buttons = {} as Record<GamepadButtonName, GamepadButtonState>
  for (const name of GAMEPAD_BUTTON_NAMES) {
    buttons[name] = { pressed: false, value: 0 }
  }
  return buttons
}

function hasGetGamepads(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator).getGamepads === 'function'
  )
}

/**
 * Maps a browser standard-mapping `Gamepad` to the normalized `GamepadSnapshot`.
 * Bounds-checks `buttons`/`axes` so a short/garbage array yields zeros rather
 * than throwing (MUST-26). `sampledAt` stamps both timestamps equal — Tier 1
 * adds no transport latency.
 */
export function normalizeStandardGamepad(
  pad: Gamepad,
  sampledAt: number,
): GamepadSnapshot {
  const buttons = emptyButtons()
  const padButtons = (pad.buttons ?? []) as ReadonlyArray<GamepadButton | undefined>
  STANDARD_BUTTON_ORDER.forEach((name, index) => {
    if (name === null) return
    const button = padButtons[index]
    if (!button) return
    const pressed = Boolean(button.pressed)
    buttons[name] = {
      pressed,
      value: clamp(finiteOr(button.value, pressed ? 1 : 0), 0, 1),
    }
  })

  const padAxes = (pad.axes ?? []) as ReadonlyArray<number | undefined>
  const axes: GamepadAxes = {
    leftX: clamp(finiteOr(padAxes[0], 0), -1, 1),
    leftY: clamp(finiteOr(padAxes[1], 0), -1, 1),
    rightX: clamp(finiteOr(padAxes[2], 0), -1, 1),
    rightY: clamp(finiteOr(padAxes[3], 0), -1, 1),
  }

  return {
    index: pad.index,
    id: typeof pad.id === 'string' ? pad.id : '',
    connected: pad.connected !== false,
    source: 'web-gamepad',
    standardMapping: pad.mapping === 'standard',
    buttons,
    axes,
    sourceTimestamp: sampledAt,
    deliveredTimestamp: sampledAt,
  }
}

/**
 * Tier 1 reader: reads `navigator.getGamepads()` live on the game's own rAF and
 * surfaces connect/disconnect from the game context's window events. Zero added
 * latency — never crosses the host bus.
 */
export class WebGamepadReader implements GamepadReader {
  private rafId: number | null = null
  private connectedListener: ((event: GamepadEvent) => void) | null = null
  private disconnectedListener: ((event: GamepadEvent) => void) | null = null
  private started = false

  read(): GamepadSnapshot[] {
    if (!hasGetGamepads()) return []
    const pads = (navigator as Navigator).getGamepads()
    const sampledAt = nowMs()
    const snapshots: GamepadSnapshot[] = []
    for (const pad of pads) {
      if (!pad || pad.connected === false) continue
      snapshots.push(normalizeStandardGamepad(pad, sampledAt))
    }
    return snapshots
  }

  start(
    onSnapshot: (snapshot: GamepadSnapshot) => void,
    onConnected: (event: GamepadConnectionEvent) => void,
    onDisconnected: (event: GamepadConnectionEvent) => void,
  ): void {
    if (this.started) this.stop()
    this.started = true

    this.connectedListener = (event: GamepadEvent): void => {
      onConnected(toConnectionEvent(event.gamepad))
    }
    this.disconnectedListener = (event: GamepadEvent): void => {
      onDisconnected(toConnectionEvent(event.gamepad))
    }
    window.addEventListener('gamepadconnected', this.connectedListener)
    window.addEventListener('gamepaddisconnected', this.disconnectedListener)

    // The Web Gamepad API does not re-fire `gamepadconnected` for pads already
    // present when the listener attaches, so emit a synthetic connect for each
    // (MUST-7).
    for (const snapshot of this.read()) {
      onConnected({
        index: snapshot.index,
        id: snapshot.id,
        source: snapshot.source,
      })
    }

    const loop = (): void => {
      for (const snapshot of this.read()) onSnapshot(snapshot)
      this.rafId = requestAnimationFrame(loop)
    }
    this.rafId = requestAnimationFrame(loop)
  }

  stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    if (this.connectedListener) {
      window.removeEventListener('gamepadconnected', this.connectedListener)
      this.connectedListener = null
    }
    if (this.disconnectedListener) {
      window.removeEventListener('gamepaddisconnected', this.disconnectedListener)
      this.disconnectedListener = null
    }
    this.started = false
  }
}

function toConnectionEvent(pad: Gamepad): GamepadConnectionEvent {
  return {
    index: pad.index,
    id: typeof pad.id === 'string' ? pad.id : '',
    source: 'web-gamepad',
  }
}
